import { all, call, put, select, takeEvery } from "redux-saga/effects";
import queryString from "query-string";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addCarePlanEquipmentFailure,
  addCarePlanEquipmentSuccess,
  deleteCarePlanEquipmentFailure,
  deleteCarePlanEquipmentSuccess,
  editCarePlanEquipmentFailure,
  editCarePlanEquipmentSuccess,
  fetchActiveEquipmentRegisterFailure,
  fetchActiveEquipmentRegisterSuccess,
  fetchCarePlanEquipmentCatalogueFailure,
  fetchCarePlanEquipmentCatalogueSuccess,
  fetchCarePlanEquipmentFailure,
  fetchCarePlanEquipmentSuccess,
  markDiscontinuedCarePlanEquipmentFailure,
  markDiscontinuedCarePlanEquipmentSuccess,
  markProvidedCarePlanEquipmentFailure,
  markProvidedCarePlanEquipmentSuccess,
  markReplacedCarePlanEquipmentFailure,
  markReplacedCarePlanEquipmentSuccess,
} from "./actions";
import {
  ADD_CARE_PLAN_EQUIPMENT,
  DELETE_CARE_PLAN_EQUIPMENT,
  EDIT_CARE_PLAN_EQUIPMENT,
  FETCH_ACTIVE_EQUIPMENT_REGISTER,
  FETCH_CARE_PLAN_EQUIPMENT,
  FETCH_CARE_PLAN_EQUIPMENT_CATALOGUE,
  MARK_DISCONTINUED_CARE_PLAN_EQUIPMENT,
  MARK_PROVIDED_CARE_PLAN_EQUIPMENT,
  MARK_REPLACED_CARE_PLAN_EQUIPMENT,
} from "./constants";
import {
  buildCarePlanEquipmentEditPayload,
  buildCarePlanEquipmentPayload,
} from "./schema";

const API_BASE = "/api/rd-care-plan-equipment";
const CATALOGUE_BASE = "/api/rd-care-plan-equipment-catalogue";

function* fetchCarePlanEquipmentSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? queryString.stringify(payload) : "";
  try {
    const { data } = yield call(api(token).get, `${API_BASE}?${query}`);
    yield put(fetchCarePlanEquipmentSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchCarePlanEquipmentFailure());
    yield put(errorAlert(e?.error || "Error fetching care plan equipment"));
  }
}

// Resident-wide ACTIVE register snapshot for dedup hints. Best-effort: fails
// silently (no alert) — every consumer no-ops when the slice is empty.
function* fetchActiveEquipmentRegisterSaga({ residentId, onSuccess }) {
  if (!residentId) return;
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = queryString.stringify({
    resident_id: residentId,
    view: "active",
    limit: 200,
  });
  try {
    const { data } = yield call(api(token).get, `${API_BASE}?${query}`);
    yield put(fetchActiveEquipmentRegisterSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch {
    yield put(fetchActiveEquipmentRegisterFailure());
  }
}

function* fetchCarePlanEquipmentCatalogueSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? queryString.stringify(payload) : "";
  try {
    const { data } = yield call(api(token).get, `${CATALOGUE_BASE}?${query}`);
    yield put(fetchCarePlanEquipmentCatalogueSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchCarePlanEquipmentCatalogueFailure());
    yield put(errorAlert(e?.error || "Error fetching equipment catalogue"));
  }
}

function* addCarePlanEquipmentSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `${API_BASE}/add`,
      buildCarePlanEquipmentPayload(payload),
    );
    yield put(addCarePlanEquipmentSuccess(data));
    yield put(successAlert("Equipment added"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(addCarePlanEquipmentFailure());
    yield put(errorAlert(e?.error || "Error adding equipment"));
  }
}

function* editCarePlanEquipmentSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, data: formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `${API_BASE}/edit/${id}`,
      buildCarePlanEquipmentEditPayload(formData),
    );
    yield put(editCarePlanEquipmentSuccess(data));
    yield put(successAlert("Equipment updated"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(editCarePlanEquipmentFailure());
    yield put(errorAlert(e?.error || "Error updating equipment"));
  }
}

function* deleteCarePlanEquipmentSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(api(token).delete, `${API_BASE}/${payload}`);
    yield put(deleteCarePlanEquipmentSuccess(data));
    yield put(successAlert("Equipment removed"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(deleteCarePlanEquipmentFailure());
    yield put(errorAlert(e?.error || "Error removing equipment"));
  }
}

function* markProvidedSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `${API_BASE}/mark-provided/${payload}`,
      {},
    );
    yield put(markProvidedCarePlanEquipmentSuccess(data));
    yield put(successAlert("Equipment marked as provided"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(markProvidedCarePlanEquipmentFailure());
    yield put(errorAlert(e?.error || "Could not mark equipment as provided"));
  }
}

function* markDiscontinuedSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, data: formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `${API_BASE}/mark-discontinued/${id}`,
      formData,
    );
    yield put(markDiscontinuedCarePlanEquipmentSuccess(data));
    yield put(successAlert("Equipment marked as discontinued"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(markDiscontinuedCarePlanEquipmentFailure());
    yield put(
      errorAlert(e?.error || "Could not mark equipment as discontinued"),
    );
  }
}

function* markReplacedSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, data: formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `${API_BASE}/mark-replaced/${id}`,
      formData,
    );
    yield put(markReplacedCarePlanEquipmentSuccess(data));
    yield put(successAlert("Equipment marked as replaced"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(markReplacedCarePlanEquipmentFailure());
    yield put(errorAlert(e?.error || "Could not mark equipment as replaced"));
  }
}

export default function* CarePlanEquipmentSaga() {
  yield all([
    takeEvery(FETCH_CARE_PLAN_EQUIPMENT, fetchCarePlanEquipmentSaga),
    takeEvery(
      FETCH_ACTIVE_EQUIPMENT_REGISTER,
      fetchActiveEquipmentRegisterSaga,
    ),
    takeEvery(
      FETCH_CARE_PLAN_EQUIPMENT_CATALOGUE,
      fetchCarePlanEquipmentCatalogueSaga,
    ),
    takeEvery(ADD_CARE_PLAN_EQUIPMENT, addCarePlanEquipmentSaga),
    takeEvery(EDIT_CARE_PLAN_EQUIPMENT, editCarePlanEquipmentSaga),
    takeEvery(DELETE_CARE_PLAN_EQUIPMENT, deleteCarePlanEquipmentSaga),
    takeEvery(MARK_PROVIDED_CARE_PLAN_EQUIPMENT, markProvidedSaga),
    takeEvery(MARK_DISCONTINUED_CARE_PLAN_EQUIPMENT, markDiscontinuedSaga),
    takeEvery(MARK_REPLACED_CARE_PLAN_EQUIPMENT, markReplacedSaga),
  ]);
}
